/**
 * Main Application Entry Point
 *
 * This is the root entry point for the React application. It initializes the React DOM
 * and renders the main App component. This file handles the initial setup and mounting
 * of the application.
 *
 * @author Andres Nava
 * @version 1.0.0
 */
// Dev :5173 — drop stale SW before other imports (see dev-boot.ts; keep out of index.html inline scripts).
import './dev-boot';
// Initialize React first, before any other imports
import './utils/reactInit';
import './utils/ensureReact';
import { patchReactDomMutations } from './utils/patchReactDomMutations';
// Prime Emotion before MUI style chunks (Vite dep optimizer ordering; see vitejs/vite#22499).
import '@emotion/react';
import '@emotion/styled';
import { logger } from "./utils/logger";
import { sessionMonitor } from './services/SessionMonitorService';
import { registerSessionMonitor } from '../shared/services/api/api.js';
import { loadRuntimePlatformFlags } from './utils/runtimePlatformFlags';

/**
 * Defer Sentry until idle / after load so the homepage critical path does not
 * download the SDK (+ unused replay/feedback when tree-shaking fails on star imports).
 */
const bootSentry = (): void => {
    void import('./config/sentry')
        .then(({ initializeSentry }) => {
            initializeSentry();
        })
        .catch((err) => {
            logger.warn('Sentry failed to load', { arg0: err });
        });
};
if (typeof window !== 'undefined') {
    const isPublicHome =
        window.location.pathname === '/' || window.location.pathname === '';
    // Public homepage: keep Sentry off the first ~1.5s critical chain (idle 8s / load+5s).
    const sentryIdleTimeoutMs = isPublicHome ? 8000 : 2500;
    const sentryLoadDelayMs = isPublicHome ? 5000 : 0;
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(bootSentry, { timeout: sentryIdleTimeoutMs });
    } else {
        window.addEventListener('load', () => {
            window.setTimeout(bootSentry, sentryLoadDelayMs);
        }, { once: true });
    }
}
registerSessionMonitor(sessionMonitor);
void loadRuntimePlatformFlags();
// Import logger for production-safe logging
// import { logger } from "./utils/logger";
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
// Defensive check: Ensure React has all required methods before proceeding
// This is a fallback safety measure for chunk loading order issues
const validateReactAvailability = (): void => {
    // Check if React is available and has all required methods
    const requiredMethods = ['useState', 'useLayoutEffect', 'useEffect', 'createContext', 'createElement'];
    const missingMethods: string[] = [];
    for (const method of requiredMethods) {
        if (!React || typeof (React as any)[method] !== 'function') {
            missingMethods.push(method);
        }
    }
    if (missingMethods.length > 0) {
        logger.error('React is missing required methods', new Error('Missing React methods'), { "arg0": { missingMethods, reactObject: React } });
        // Try to get React from window if available (from another chunk)
        if (typeof window !== 'undefined' && (window as any).React) {
            const windowReact = (window as any).React;
            let allMethodsAvailable = true;
            for (const method of requiredMethods) {
                if (typeof windowReact[method] !== 'function') {
                    allMethodsAvailable = false;
                    break;
                }
            }
            if (allMethodsAvailable) {
                logger.warn('Using React from window object as fallback');
                // Note: We can't reassign the imported React, but we can ensure window.React is set
                return; // Continue with window.React available
            }
        }
        throw new Error(`React is missing required methods: ${missingMethods.join(', ')}. This may indicate a chunk loading issue.`);
    }
};
// Ensure React is available globally for Material-UI
if (typeof window !== 'undefined') {
    (window as any).React = React;
    (window as any).ReactDOM = ReactDOM;
    // Also ensure React is available on globalThis
    (globalThis as any).React = React;
    (globalThis as any).ReactDOM = ReactDOM;
    // Ensure React is available on the global object as well (browser-compatible)
    if (typeof globalThis !== 'undefined') {
        (globalThis as any).React = React;
        (globalThis as any).ReactDOM = ReactDOM;
    }
}
// Validate React availability (defensive check for chunk loading issues)
validateReactAvailability();
// Debug React imports (can be removed in production)
// Console logs removed to prevent development popups
import App from './App';
import './index.css';
import './styles/missionMode.css';
import './utils/impersonationUtils'; // Import impersonation utilities
import './utils/clearAuth'; // Import clear auth utility
import { initializeBrowserCompatibility } from './utils/browserCompatibility';
import { applyErrorSuppression } from './utils/errorSuppression';
import { initConsoleCapture } from './utils/consoleCapture';
// JWT auto-fix removed - using session-based authentication
import { initializeErrorFilter } from './utils/errorFilter';
import { initWebVitals } from './utils/webVitals';
// CSS import fix for third-party libraries
// import './utils/cssImportFix';
// Ensure React is properly initialized and prevent multiple instances
if (typeof React === 'undefined' || !React.useState) {
    logger.error('React is undefined or useState is not available', undefined, { "arg0": { reactObject: React } });
    throw new Error('React is not properly loaded. Please check your dependencies.');
}
// Ensure ReactDOM is properly initialized
if (typeof ReactDOM === 'undefined' || !ReactDOM.createRoot) {
    logger.error('ReactDOM is undefined or createRoot is not available', undefined, { "arg0": { reactDOMObject: ReactDOM } });
    throw new Error('ReactDOM is not properly loaded. Please check your dependencies.');
}
// Ensure React context is properly initialized
if (!React.useContext) {
    logger.error('React.useContext is not available', undefined, {});
    throw new Error('React.useContext is not available. React may not be properly loaded.');
}
const isDevHost = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    return import.meta.env.DEV || window.location.port === '5173';
};
const unregisterServiceWorkersOnDevHost = async (): Promise<void> => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !isDevHost()) {
        return;
    }
    try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (!registrations.length) {
            return;
        }
        await Promise.all(
            registrations.map(async (registration) => {
                try {
                    await registration.unregister();
                }
                catch (error) {
                    logger.warn('Dev SW unregister skipped for one registration', { "arg0": error });
                }
            }),
        );
        logger.debug('Dev host: service workers unregistered to avoid stale module caching');
    }
    catch (error) {
        // Non-blocking by design: app startup must continue.
        logger.warn('Dev host: unable to query service worker registrations', { "arg0": error });
    }
};
// Store React instance to prevent corruption
const ReactInstance = React;
if (typeof window !== 'undefined') {
    (window as any).__REACT_INSTANCE__ = ReactInstance;
}
// Verify React is working by testing context creation
try {
    const testContext = React.createContext('test');
    // React context creation test passed
}
catch (error) {
    logger.error('React context creation test failed', error, {});
    throw new Error('React context creation is not working properly');
}
// Initialize comprehensive error filtering for browser extensions and third-party scripts
// Apply error suppression AFTER React is confirmed to be loaded
initializeErrorFilter();
const restoreErrorSuppression = applyErrorSuppression();
// Note: We don't call restoreErrorSuppression() to keep error suppression active for the entire app session
// Initialize tracking prevention early to suppress warnings before app loads
import { initializeTrackingPrevention } from './utils/trackingPreventionInit';
initializeTrackingPrevention();
// Add optimized global error handlers for unhandled promise rejections and errors
const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const message = event.reason?.message || event.reason?.toString() || '';
    const stack = event.reason?.stack?.toString() || '';
    // Suppress extension-related unhandled promise rejections
    if (message.includes('FrameDoesNotExistError') ||
        message.includes('Frame 0 does not exist') ||
        message.includes(' does not exist in tab ') ||
        message.includes('disconnected port object') ||
        message.includes('chrome-extension://') ||
        message.includes('moz-extension://') ||
        message.includes('safari-extension://') ||
        message.includes('mfbcdcnpokpoajjciilocoachedjkima') ||
        message.includes('Could not establish connection') ||
        message.includes('message port closed') ||
        message.includes('content_script.js') ||
        stack.includes('content_script.js') ||
        stack.includes('shouldOfferCompletionListForField') ||
        message.includes("Cannot read properties of undefined (reading 'control')") ||
        message.includes("Cannot read properties of null (reading 'postMessage')") ||
        message.includes('Error handling response: TypeError') ||
        stack.includes('/cdn-cgi/zaraz/') ||
        stack.includes('cdn-cgi/zaraz') ||
        message.includes('cdn-cgi/zaraz')) {
        event.preventDefault(); // Prevent the error from being logged
        return;
    }
};
// Use optimized event listener with microtask
Promise.resolve().then(() => {
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
});
// Add optimized error handler
const handleError = (event: ErrorEvent) => {
    const message = event.message || event.error?.message || '';
    const filename = event.filename || '';
    const stack = event.error?.stack?.toString() || '';
    // Suppress extension-related errors
    if (message.includes('FrameDoesNotExistError') ||
        message.includes('Frame 0 does not exist') ||
        message.includes(' does not exist in tab ') ||
        message.includes('disconnected port object') ||
        message.includes('chrome-extension://') ||
        message.includes('moz-extension://') ||
        message.includes('safari-extension://') ||
        message.includes('mfbcdcnpokpoajjciilocoachedjkima') ||
        message.includes('Could not establish connection') ||
        message.includes('message port closed') ||
        filename.includes('content_script.js') ||
        filename.includes('chrome-extension://') ||
        filename.includes('moz-extension://') ||
        filename.includes('safari-extension://') ||
        message.includes('content_script.js') ||
        stack.includes('content_script.js') ||
        stack.includes('shouldOfferCompletionListForField') ||
        message.includes("Cannot read properties of undefined (reading 'control')") ||
        message.includes("Cannot read properties of null (reading 'postMessage')") ||
        message.includes('Error handling response: TypeError') ||
        filename.includes('/cdn-cgi/zaraz/') ||
        stack.includes('/cdn-cgi/zaraz/') ||
        stack.includes('cdn-cgi/zaraz')) {
        event.preventDefault(); // Prevent the error from being logged
        return;
    }
};
// Suppress extension-injected resource load errors (capture phase)
const handleResourceError = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (!target) {
        return;
    }
    const src = 'src' in target ? String((target as HTMLScriptElement).src || '') : '';
    const href = 'href' in target ? String((target as HTMLLinkElement).href || '') : '';
    const resourceUrl = src || href;
    if (!resourceUrl) {
        return;
    }
    const lowerUrl = resourceUrl.toLowerCase();
    if (lowerUrl.includes('chrome-extension://') ||
        lowerUrl.includes('moz-extension://') ||
        lowerUrl.includes('safari-extension://') ||
        lowerUrl.includes('ms-browser-extension://') ||
        lowerUrl.includes('mfbcdcnpokpoajjciilocoachedjkima') ||
        lowerUrl.includes('completion_list.html')) {
        event.preventDefault();
        event.stopImmediatePropagation();
    }
};
// Use optimized event listener with microtask
Promise.resolve().then(() => {
    window.addEventListener('error', handleError);
    window.addEventListener('error', handleResourceError, true);
});
// Initialize browser compatibility with optimized performance
Promise.resolve().then(() => {
    if (window.requestIdleCallback) {
        window.requestIdleCallback(() => initializeBrowserCompatibility(), { timeout: 200 });
    }
    else {
        initializeBrowserCompatibility();
    }
});
// Additional early error suppression for extension errors
// Guard to prevent infinite recursion
let isProcessingEarlyError = false;
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
const originalConsoleLog = console.log;

// Helper to check if a message should be suppressed
// This must be defined before console.error/warn/log overrides
const shouldSuppress = (message: string): boolean => {
    const lowerMessage = message.toLowerCase();
    
    // WebSocket/HMR connection errors - suppress in all console methods
    if (lowerMessage.includes('websocket connection') ||
        lowerMessage.includes('websocket connection to') ||
        lowerMessage.includes('websocket is closed before the connection is established') ||
        lowerMessage.includes('error during websocket handshake') ||
        lowerMessage.includes('unexpected response code: 200') ||
        lowerMessage.includes('unexpected response code') ||
        lowerMessage.includes('websocket handshake') ||
        lowerMessage.includes('failed to establish websocket') ||
        lowerMessage.includes('websocket upgrade failed') ||
        lowerMessage.includes('websocket connection failed') ||
        lowerMessage.includes('websocket error') ||
        lowerMessage.includes('websocket closed') ||
        (lowerMessage.includes('wss://') && (lowerMessage.includes('failed') || lowerMessage.includes('error') || lowerMessage.includes('closed'))) ||
        (lowerMessage.includes('ws://') && (lowerMessage.includes('failed') || lowerMessage.includes('error') || lowerMessage.includes('closed'))) ||
        (lowerMessage.includes('socket.io') && (lowerMessage.includes('failed') || lowerMessage.includes('error') || lowerMessage.includes('handshake') || lowerMessage.includes('closed'))) ||
        lowerMessage.includes('could not establish connection') ||
        (lowerMessage.includes('connection') && lowerMessage.includes('failed') && (lowerMessage.includes('5173') || lowerMessage.includes('socket')))) {
        return true;
    }
    
    // Tracking prevention messages - expanded patterns (most common first for performance)
    if (lowerMessage.includes('tracking prevention blocked access to storage') ||
        lowerMessage.includes('tracking prevention blocked access') ||
        lowerMessage.includes('blocked access to storage for') ||
        lowerMessage.includes('blocked access to storage') ||
        lowerMessage.includes('storage access blocked') ||
        lowerMessage.includes('storage for <url>') ||
        lowerMessage.includes('for <url>') ||
        lowerMessage.includes('tracking prevention blocked') ||
        message.includes('Tracking Prevention blocked access to storage for <URL>') ||
        message.includes('Tracking Prevention blocked access to storage') ||
        (lowerMessage.includes('tracking prevention') && lowerMessage.includes('blocked')) ||
        (lowerMessage.includes('tracking_prevention')) ||
        // Exact match patterns (case sensitive)
        message.includes('Tracking Prevention blocked access to storage for') ||
        message.includes('Tracking Prevention blocked access') ||
        // Criteo and other third-party tracking prevention warnings
        lowerMessage.includes('dis.criteo.com') ||
        lowerMessage.includes('criteo.com/dis/usersync') ||
        (lowerMessage.includes('tracking prevention') && lowerMessage.includes('criteo')) ||
        // Third-party tracking domain patterns
        (lowerMessage.includes('tracking prevention blocked') && (
            lowerMessage.includes('dis.criteo.com') ||
            lowerMessage.includes('doubleclick') ||
            lowerMessage.includes('google-analytics')
        ))) {
        return true;
    }
    
    // Browser violation warnings (performance warnings) - expanded patterns
    if (lowerMessage.includes('[violation]') ||
        lowerMessage.includes('violation]') ||
        (lowerMessage.includes("'message' handler took") && lowerMessage.includes('ms')) ||
        (lowerMessage.includes("'load' handler took") && lowerMessage.includes('ms')) ||
        (lowerMessage.includes("'unload' handler took") && lowerMessage.includes('ms')) ||
        (lowerMessage.includes("'resize' handler took") && lowerMessage.includes('ms')) ||
        (lowerMessage.includes("'scroll' handler took") && lowerMessage.includes('ms')) ||
        (lowerMessage.includes("'requestidlecallback' handler took") && lowerMessage.includes('ms')) ||
        (lowerMessage.includes("'requestidlecallback'") && lowerMessage.includes('handler took')) ||
        lowerMessage.includes('forced reflow while executing javascript') ||
        lowerMessage.includes('forced reflow') ||
        lowerMessage.includes('avoid using document.write()') ||
        lowerMessage.includes('document.write()') ||
        (lowerMessage.includes('handler took') && lowerMessage.includes('ms')) ||
        lowerMessage.includes('long running') ||
        lowerMessage.includes('slow operation')) {
        return true;
    }
    
    // Browser intervention messages
    if (lowerMessage.includes('[intervention]') ||
        lowerMessage.includes('intervention]') ||
        lowerMessage.includes('images loaded lazily') ||
        lowerMessage.includes('load events are deferred')) {
        return true;
    }
    
    // Suppress verbose logger debug messages that end with "undefined"
    // These are typically from logger.debug() calls with no data
    if (message.includes('🔍') && (message.trim().endsWith('undefined') || message.includes(' undefined'))) {
        return true;
    }
    
    // Suppress very verbose navigation/environment debug messages
    // These are repetitive and not useful for most debugging
    if (lowerMessage.includes('isdevelopmentenvironment [client]') ||
        lowerMessage.includes('getnavigationurl') ||
        lowerMessage.includes('getsubdomainurl') ||
        (lowerMessage.includes('isdevelopmentenvironment') && lowerMessage.includes('hostname'))) {
        return true;
    }
    
    return false;
};

console.error = (...args: any[]) => {
    // Prevent infinite recursion
    if (isProcessingEarlyError) {
        return;
    }
    try {
        isProcessingEarlyError = true;
        const message = args.join(' ');
        const lowerMessage = message.toLowerCase();
        // Filter out stack overflow errors immediately to prevent recursion
        if (lowerMessage.includes('maximum call stack size exceeded') ||
            lowerMessage.includes('rangeerror') ||
            lowerMessage.includes('web_monetization_wall') ||
            lowerMessage.includes('focustrap.js') ||
            lowerMessage.includes('contain @ focustrap')) {
            return;
        }
        // Check for runtime.lastError variations first (most common, case-insensitive)
        if (lowerMessage.includes('runtime.lasterror') || lowerMessage.includes('runtime.last_error')) {
            return;
        }
        // Use shouldSuppress helper for consistency across all console methods
        if (shouldSuppress(message)) {
            return;
        }
        // Filter out extension errors that might occur before main initialization
        const extensionErrorPatterns = [
            'runtime.lastError',
            'The message port closed before a response was received',
            'Attempting to use a disconnected port object',
            'Unchecked runtime.lastError',
            'Unchecked runtime.lastError:',
            'Error handling response: Error: Attempting to use a disconnected port object',
            'chrome-extension://',
            'moz-extension://',
            'safari-extension://',
            'net::ERR_FILE_NOT_FOUND',
            'ERR_FILE_NOT_FOUND',
            'content_script.js',
            'background.js',
            'FrameDoesNotExistError',
            'Could not establish connection',
            'Could not establish connection. Receiving end does not exist',
            'Receiving end does not exist',
            'utils.js',
            'extensionState.js',
            'heuristicsRedefinitions.js',
            'DelayedMessageSender',
            ' does not exist in tab ',
            "Cannot read properties of null (reading 'postMessage')",
            'Error handling response: TypeError',
            'shouldOfferCompletionListForField',
            'elementWasFocused',
            'focusInEventHandler',
            'processInputEvent',
            "Cannot read properties of undefined (reading 'control')",
            // Google Ads / DoubleClick third-party 404s (cannot fix; from googleads.g.doubleclick.net)
            'zrt_lookup',
            'zrt_lookup.html',
            'googleads.g.doubleclick',
            'pagead/html'
        ];
        const isExtensionError = extensionErrorPatterns.some(pattern => lowerMessage.includes(pattern.toLowerCase()));
        if (!isExtensionError) {
            originalConsoleError.apply(console, args);
        }
    }
    finally {
        isProcessingEarlyError = false;
    }
};
// Suppress console.warn and console.log for tracking prevention messages and browser violations
// These must be set up early to catch browser-generated messages
console.warn = (...args: any[]) => {
    const message = args.join(' ');
    if (!shouldSuppress(message)) {
        originalConsoleWarn.apply(console, args);
    }
};

console.log = (...args: any[]) => {
    const message = args.join(' ');
    if (!shouldSuppress(message)) {
        originalConsoleLog.apply(console, args);
    }
};
// Initialize console capture after suppression wrappers are in place
initConsoleCapture(100);
void unregisterServiceWorkersOnDevHost();
// JWT auto-fix removed - session-based authentication doesn't need JWT signature fixes
const container = document.getElementById('root');
if (!container) {
    throw new Error('Failed to find the root element');
}
// Server injects a visually hidden <h1> in #root for no-JS crawlers (seoHtmlInjection). Remove it
// before React mounts so JS-enabled audits only see the page's real heading (fixes duplicate H1).
container.querySelector('[data-apt-seo-heading="1"]')?.remove();
patchReactDomMutations();
const root = ReactDOM.createRoot(container);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
// Store console reference before any replacements (for diagnostics)
const diagnosticConsole = typeof window !== 'undefined' ? (window as any).console : null;
// Render immediately but ensure React is fully loaded
try {
    logger.debug('[DIAGNOSTIC] React app - STARTING RENDER');
    if (import.meta.env.DEV && diagnosticConsole) {
        diagnosticConsole.log('[DIAGNOSTIC] React app - STARTING RENDER');
    }
    root.render(<React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>);
    if (import.meta.env.DEV && typeof window !== 'undefined') {
        (window as unknown as { __APT_DEV_APP_MOUNTED?: boolean }).__APT_DEV_APP_MOUNTED = true;
        window.dispatchEvent(new CustomEvent('apt-dev-app-mounted'));
    }
    initWebVitals();
    logger.debug('[DIAGNOSTIC] React app - RENDER INITIATED');
    if (import.meta.env.DEV && diagnosticConsole) {
        diagnosticConsole.log('[DIAGNOSTIC] React app - RENDER INITIATED');
    }
    // React app rendered successfully
    // Debug: Check if content is in DOM (development only)
    // Console logs removed to prevent development popups
}
catch (error) {
    logger.error('[DIAGNOSTIC] React app - RENDER ERROR', error as Error);
    // Use stored console reference for diagnostics (bypasses safe-console-replacement script)
    if (diagnosticConsole) {
        diagnosticConsole.error('[DIAGNOSTIC] React app - RENDER ERROR', error);
    }
    logger.error('Failed to render React app', error as Error, {});
    // Safe fallback (no innerHTML) — error messages may contain unsanitized exception text
    container.replaceChildren();
    const wrap = document.createElement('div');
    wrap.style.padding = '20px';
    wrap.style.fontFamily = 'Arial, sans-serif';
    const h1 = document.createElement('h1');
    h1.textContent = 'Application Error';
    const p1 = document.createElement('p');
    p1.textContent = 'Failed to load the application. Please refresh the page.';
    const p2 = document.createElement('p');
    p2.textContent =
      `Error: ${error instanceof Error ? error.message : String(error)}`;
    wrap.append(h1, p1, p2);
    container.appendChild(wrap);
}
